import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axiosInstance from '../utils/axiosConfig';
import ProfilePhotoEditor from './ProfilePhotoEditor';
import './ManageAccount.css';
import './UserAvatar.css';

const MONTH_OPTIONS = [
  { value: '', label: 'Month' },
  { value: '1', label: 'January' },
  { value: '2', label: 'February' },
  { value: '3', label: 'March' },
  { value: '4', label: 'April' },
  { value: '5', label: 'May' },
  { value: '6', label: 'June' },
  { value: '7', label: 'July' },
  { value: '8', label: 'August' },
  { value: '9', label: 'September' },
  { value: '10', label: 'October' },
  { value: '11', label: 'November' },
  { value: '12', label: 'December' },
];

const GENDER_OPTIONS = [
  { value: '', label: 'Select gender' },
  { value: 'Male', label: 'Male' },
  { value: 'Female', label: 'Female' },
];

const formatDate = (value) => {
  if (!value) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '—';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const formatBirthday = (user) => {
  if (!user?.birthMonth || !user?.birthDay || !user?.birthYear) return '—';
  const month = MONTH_OPTIONS.find((opt) => opt.value === String(user.birthMonth));
  if (!month?.value) return '—';
  return `${month.label} ${user.birthDay}, ${user.birthYear}`;
};

const EditIcon = () => (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25zM20.71 7.04c.39-.39.39-1.02 0-1.41l-2.34-2.34a.9959.9959 0 0 0-1.41 0l-1.83 1.83 3.75 3.75 1.83-1.83z" />
  </svg>
);

const SectionHeader = ({ title, editing, onEdit, editLabel }) => (
  <div className="manage-section-header">
    <h3 className="manage-section-title">{title}</h3>
    {!editing && (
      <button
        type="button"
        className="manage-edit-icon-btn"
        onClick={onEdit}
        aria-label={editLabel}
      >
        <EditIcon />
      </button>
    )}
  </div>
);

const buildFormFromUser = (user) => ({
  firstName: user?.firstName || '',
  lastName: user?.lastName || '',
  gender: user?.gender || '',
  birthMonth: user?.birthMonth ? String(user.birthMonth) : '',
  birthDay: user?.birthDay ? String(user.birthDay) : '',
  birthYear: user?.birthYear ? String(user.birthYear) : '',
});

const ManageAccount = ({ currentUser, onProfileUpdate }) => {
  const navigate = useNavigate();
  const [user, setUser] = useState(currentUser);
  const [form, setForm] = useState(buildFormFromUser(currentUser));
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [saveMessage, setSaveMessage] = useState({ type: '', text: '' });
  const [signature, setSignature] = useState(currentUser?.signature || '');
  const [savingSig, setSavingSig] = useState(false);
  const [sigMessage, setSigMessage] = useState({ type: '', text: '' });
  const [sigTone, setSigTone] = useState('formal');
  const [recoveryEmail, setRecoveryEmail] = useState(currentUser?.recoveryEmail || '');
  const [savingRecovery, setSavingRecovery] = useState(false);
  const [recoveryMessage, setRecoveryMessage] = useState({ type: '', text: '' });
  const [editingSection, setEditingSection] = useState(null);

  useEffect(() => {
    setUser(currentUser);
    setForm(buildFormFromUser(currentUser));
    setSignature(currentUser?.signature || '');
    setRecoveryEmail(currentUser?.recoveryEmail || '');
  }, [currentUser]);

  useEffect(() => {
    const loadProfile = async () => {
      setLoading(true);
      setError('');
      try {
        const response = await axiosInstance.get('/api/auth/me');
        if (response.data.success) {
          const loaded = response.data.data.user;
          setUser(loaded);
          setForm(buildFormFromUser(loaded));
          setSignature(loaded.signature || '');
          setRecoveryEmail(loaded.recoveryEmail || '');
        }
      } catch (err) {
        setError(err.response?.data?.message || 'Could not load account details');
      } finally {
        setLoading(false);
      }
    };

    loadProfile();
  }, []);

  const handleUserUpdate = (updatedUser) => {
    setUser(updatedUser);
    setForm(buildFormFromUser(updatedUser));
    setSignature(updatedUser?.signature || '');
    setRecoveryEmail(updatedUser?.recoveryEmail || '');
    onProfileUpdate?.(updatedUser);
  };

  const revertSection = (section) => {
    if (section === 'profile') {
      setForm(buildFormFromUser(user));
      setSaveMessage({ type: '', text: '' });
    }
    if (section === 'signature') {
      setSignature(user?.signature || '');
      setSigMessage({ type: '', text: '' });
    }
    if (section === 'recovery') {
      setRecoveryEmail(user?.recoveryEmail || '');
      setRecoveryMessage({ type: '', text: '' });
    }
  };

  const startEdit = (section) => {
    if (editingSection && editingSection !== section) {
      revertSection(editingSection);
    }
    setEditingSection(section);
  };

  const cancelEdit = (section) => {
    revertSection(section);
    setEditingSection(null);
  };

  const handleSaveRecoveryEmail = async (e) => {
    e.preventDefault();
    setSavingRecovery(true);
    setRecoveryMessage({ type: '', text: '' });

    try {
      const response = await axiosInstance.patch('/api/auth/recovery-email', {
        recoveryEmail: recoveryEmail.trim().toLowerCase(),
      });
      if (response.data.success) {
        handleUserUpdate(response.data.data.user);
        setRecoveryMessage({ type: 'success', text: 'Recovery email saved' });
        setEditingSection(null);
      } else {
        setRecoveryMessage({
          type: 'error',
          text: response.data.message || 'Could not save recovery email',
        });
      }
    } catch (err) {
      setRecoveryMessage({
        type: 'error',
        text: err.response?.data?.message || 'Could not save recovery email',
      });
    } finally {
      setSavingRecovery(false);
    }
  };

  const generateSignature = () => {
    const first = (user?.firstName || '').trim();
    const last = (user?.lastName || '').trim();
    const fullName = [first, last].filter(Boolean).join(' ') || user?.username || '';
    const email = user?.email || '';

    let text = '';
    if (sigTone === 'friendly') {
      text = `Thanks & regards,\n${first || fullName}\n${email}`;
    } else if (sigTone === 'minimal') {
      text = `${fullName}\n${email}`;
    } else {
      text = `Best regards,\n${fullName}\n${email}`;
    }

    setSignature(text);
    if (sigMessage.text) setSigMessage({ type: '', text: '' });
  };

  const handleSaveSignature = async (e) => {
    e.preventDefault();
    setSavingSig(true);
    setSigMessage({ type: '', text: '' });

    try {
      const response = await axiosInstance.patch('/api/auth/signature', { signature });
      if (response.data.success) {
        handleUserUpdate(response.data.data.user);
        setSigMessage({ type: 'success', text: 'Signature saved' });
        setEditingSection(null);
      } else {
        setSigMessage({
          type: 'error',
          text: response.data.message || 'Could not save signature',
        });
      }
    } catch (err) {
      setSigMessage({
        type: 'error',
        text: err.response?.data?.message || 'Could not save signature',
      });
    } finally {
      setSavingSig(false);
    }
  };

  const updateField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (saveMessage.text) setSaveMessage({ type: '', text: '' });
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSaveMessage({ type: '', text: '' });

    try {
      const response = await axiosInstance.patch('/api/auth/profile', {
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        gender: form.gender,
        birthMonth: Number(form.birthMonth),
        birthDay: Number(form.birthDay),
        birthYear: Number(form.birthYear),
      });

      if (response.data.success) {
        const updated = response.data.data.user;
        handleUserUpdate(updated);
        setSaveMessage({ type: 'success', text: 'Personal information saved' });
        setEditingSection(null);
      } else {
        setSaveMessage({
          type: 'error',
          text: response.data.message || 'Could not save changes',
        });
      }
    } catch (err) {
      setSaveMessage({
        type: 'error',
        text: err.response?.data?.message || 'Could not save changes',
      });
    } finally {
      setSaving(false);
    }
  };

  const displayName = [user?.firstName, user?.lastName].filter(Boolean).join(' ') || user?.username;

  return (
    <div className="manage-account-page">
      <div className="manage-account-inner">
        <header className="manage-account-header">
          <button
            type="button"
            className="manage-back-btn"
            onClick={() => navigate('/inbox')}
          >
            ← Back to inbox
          </button>
          <h1>Manage your ATOZASMail Account</h1>
          <p className="manage-account-lead">Edit your profile and account settings.</p>
        </header>

        {loading && <p className="manage-account-loading">Loading account details...</p>}
        {error && <p className="manage-account-error" role="alert">{error}</p>}

        {!loading && user && (
          <>
            <section className="manage-account-card manage-profile-card">
              <ProfilePhotoEditor user={user} onUserUpdate={handleUserUpdate} size="lg" />
              <div className="manage-profile-meta">
                <h2>{displayName}</h2>
                <p>{user.email}</p>
              </div>
            </section>

            <section className="manage-account-card">
              <SectionHeader
                title="Personal information"
                editing={editingSection === 'profile'}
                onEdit={() => startEdit('profile')}
                editLabel="Edit personal information"
              />
              {editingSection === 'profile' ? (
                <form className="manage-edit-form" onSubmit={handleSaveProfile}>
                  <div className="manage-form-grid">
                    <div className="manage-form-field">
                      <label htmlFor="firstName">First name</label>
                      <input
                        id="firstName"
                        className="manage-input"
                        value={form.firstName}
                        onChange={(e) => updateField('firstName', e.target.value)}
                        required
                        disabled={saving}
                      />
                    </div>
                    <div className="manage-form-field">
                      <label htmlFor="lastName">Last name</label>
                      <input
                        id="lastName"
                        className="manage-input"
                        value={form.lastName}
                        onChange={(e) => updateField('lastName', e.target.value)}
                        disabled={saving}
                      />
                    </div>
                    <div className="manage-form-field">
                      <label htmlFor="gender">Gender</label>
                      <select
                        id="gender"
                        className="manage-input manage-select"
                        value={form.gender}
                        onChange={(e) => updateField('gender', e.target.value)}
                        required
                        disabled={saving}
                      >
                        {GENDER_OPTIONS.map((opt) => (
                          <option key={opt.value || 'empty'} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div className="manage-form-field manage-birthday-field">
                      <label>Birthday</label>
                      <div className="manage-birthday-row">
                        <select
                          className="manage-input manage-select"
                          value={form.birthMonth}
                          onChange={(e) => updateField('birthMonth', e.target.value)}
                          required
                          disabled={saving}
                          aria-label="Birth month"
                        >
                          {MONTH_OPTIONS.map((opt) => (
                            <option key={opt.value || 'm'} value={opt.value}>
                              {opt.label}
                            </option>
                          ))}
                        </select>
                        <input
                          className="manage-input"
                          value={form.birthDay}
                          onChange={(e) =>
                            updateField('birthDay', e.target.value.replace(/\D/g, '').slice(0, 2))
                          }
                          placeholder="Day"
                          required
                          disabled={saving}
                          aria-label="Birth day"
                        />
                        <input
                          className="manage-input"
                          value={form.birthYear}
                          onChange={(e) =>
                            updateField('birthYear', e.target.value.replace(/\D/g, '').slice(0, 4))
                          }
                          placeholder="Year"
                          required
                          disabled={saving}
                          aria-label="Birth year"
                        />
                      </div>
                    </div>
                  </div>
                  {saveMessage.text && (
                    <p className={`manage-save-message ${saveMessage.type}`} role="alert">
                      {saveMessage.text}
                    </p>
                  )}
                  <div className="manage-edit-actions">
                    <button type="submit" className="manage-save-btn" disabled={saving}>
                      {saving ? 'Saving...' : 'Save changes'}
                    </button>
                    <button
                      type="button"
                      className="manage-cancel-btn"
                      onClick={() => cancelEdit('profile')}
                      disabled={saving}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  <div className="manage-form-grid">
                    <div className="manage-readonly-item">
                      <span className="manage-detail-label">First name</span>
                      <span className="manage-detail-value">{user.firstName || '—'}</span>
                    </div>
                    <div className="manage-readonly-item">
                      <span className="manage-detail-label">Last name</span>
                      <span className="manage-detail-value">{user.lastName || '—'}</span>
                    </div>
                    <div className="manage-readonly-item">
                      <span className="manage-detail-label">Gender</span>
                      <span className="manage-detail-value">{user.gender || '—'}</span>
                    </div>
                    <div className="manage-readonly-item">
                      <span className="manage-detail-label">Birthday</span>
                      <span className="manage-detail-value">{formatBirthday(user)}</span>
                    </div>
                  </div>
                  {saveMessage.text && (
                    <p className={`manage-save-message ${saveMessage.type}`} role="alert">
                      {saveMessage.text}
                    </p>
                  )}
                </>
              )}
            </section>

            <section className="manage-account-card">
              <SectionHeader
                title="Email signature"
                editing={editingSection === 'signature'}
                onEdit={() => startEdit('signature')}
                editLabel="Edit email signature"
              />
              {editingSection === 'signature' ? (
                <form className="manage-edit-form" onSubmit={handleSaveSignature}>
                  <div className="manage-sig-generator">
                    <select
                      className="manage-input manage-select"
                      value={sigTone}
                      onChange={(e) => setSigTone(e.target.value)}
                      disabled={savingSig}
                      aria-label="Signature tone"
                    >
                      <option value="formal">Formal</option>
                      <option value="friendly">Friendly</option>
                      <option value="minimal">Minimal</option>
                    </select>
                    <button
                      type="button"
                      className="manage-generate-btn"
                      onClick={generateSignature}
                      disabled={savingSig}
                    >
                      ✨ Generate
                    </button>
                  </div>
                  <div className="manage-form-field">
                    <label htmlFor="signature">Added to the bottom of new emails</label>
                    <textarea
                      id="signature"
                      className="manage-input manage-signature-input"
                      rows={5}
                      value={signature}
                      onChange={(e) => {
                        setSignature(e.target.value);
                        if (sigMessage.text) setSigMessage({ type: '', text: '' });
                      }}
                      placeholder={'Regards,\nYour Name\nATOZASMail'}
                      maxLength={5000}
                      disabled={savingSig}
                    />
                  </div>
                  {sigMessage.text && (
                    <p className={`manage-save-message ${sigMessage.type}`} role="alert">
                      {sigMessage.text}
                    </p>
                  )}
                  <div className="manage-edit-actions">
                    <button type="submit" className="manage-save-btn" disabled={savingSig}>
                      {savingSig ? 'Saving...' : 'Save signature'}
                    </button>
                    <button
                      type="button"
                      className="manage-cancel-btn"
                      onClick={() => cancelEdit('signature')}
                      disabled={savingSig}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  <p className="manage-section-help">Added to the bottom of new emails</p>
                  <p className={`manage-readonly-text ${user.signature ? '' : 'is-empty'}`}>
                    {user.signature || 'No signature yet'}
                  </p>
                  {sigMessage.text && (
                    <p className={`manage-save-message ${sigMessage.type}`} role="alert">
                      {sigMessage.text}
                    </p>
                  )}
                </>
              )}
            </section>

            <section className="manage-account-card">
              <SectionHeader
                title="Recovery email"
                editing={editingSection === 'recovery'}
                onEdit={() => startEdit('recovery')}
                editLabel="Edit recovery email"
              />
              <p className="manage-section-help">
                Password reset links are sent to this external address (Gmail, Yahoo, etc.).
              </p>
              {editingSection === 'recovery' ? (
                <form className="manage-edit-form" onSubmit={handleSaveRecoveryEmail}>
                  <div className="manage-form-field">
                    <label htmlFor="recoveryEmail">Recovery email</label>
                    <input
                      id="recoveryEmail"
                      type="email"
                      className="manage-input"
                      value={recoveryEmail}
                      onChange={(e) => {
                        setRecoveryEmail(e.target.value);
                        if (recoveryMessage.text) setRecoveryMessage({ type: '', text: '' });
                      }}
                      placeholder="you@gmail.com"
                      required
                      disabled={savingRecovery}
                    />
                  </div>
                  {recoveryMessage.text && (
                    <p className={`manage-save-message ${recoveryMessage.type}`} role="alert">
                      {recoveryMessage.text}
                    </p>
                  )}
                  <div className="manage-edit-actions">
                    <button type="submit" className="manage-save-btn" disabled={savingRecovery}>
                      {savingRecovery ? 'Saving...' : 'Save recovery email'}
                    </button>
                    <button
                      type="button"
                      className="manage-cancel-btn"
                      onClick={() => cancelEdit('recovery')}
                      disabled={savingRecovery}
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              ) : (
                <>
                  <div className="manage-readonly-item">
                    <span className="manage-detail-label">Recovery email</span>
                    <span className="manage-detail-value">
                      {user.recoveryEmail || 'Not set'}
                    </span>
                  </div>
                  {recoveryMessage.text && (
                    <p className={`manage-save-message ${recoveryMessage.type}`} role="alert">
                      {recoveryMessage.text}
                    </p>
                  )}
                </>
              )}
            </section>

            <section className="manage-account-card">
              <h3 className="manage-section-title">Account information</h3>
              <div className="manage-readonly-list">
                <div className="manage-readonly-item">
                  <span className="manage-detail-label">Email address</span>
                  <span className="manage-detail-value">{user.email}</span>
                </div>
                <div className="manage-readonly-item">
                  <span className="manage-detail-label">Username</span>
                  <span className="manage-detail-value">{user.username}</span>
                </div>
                <div className="manage-readonly-item">
                  <span className="manage-detail-label">Account created</span>
                  <span className="manage-detail-value">{formatDate(user.createdAt)}</span>
                </div>
              </div>
              <button
                type="button"
                className="manage-password-row"
                onClick={() => navigate('/account/password')}
              >
                <span className="manage-password-label">Password</span>
                <span className="manage-password-action">
                  Change
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z" />
                  </svg>
                </span>
              </button>
            </section>
          </>
        )}
      </div>
    </div>
  );
};

export default ManageAccount;
